/***
*
*   FEEDBACK FILTERS PANEL
*   Popover panel for filtering feedback by rating, date range, and message presence.
*   Shared UI primitives (FilterSectionLabel, DateRangeSection, etc.) can be promoted
*   to a shared component when users.js and accounts.js adopt the same structure.
*
**********/

import { RATING_VALUES, RATING_LABELS, HAS_MESSAGE_OPTIONS, TEXT } from './constants';
import Style from './filters.tailwind.js';

// ─── UI primitives ────────────────────────────────────────────────────────────

function FilterSectionLabel({ children }) {
  return <div className={Style.label}>{children}</div>;
}

function DateRangeSection({ dateFrom, dateTo, onFromChange, onToChange }) {
  return (
    <div className={Style.dateGroup}>
      <div className={Style.dateRow}>
        <span className={Style.dateRowLabel}>{TEXT.filterDateFrom}</span>
        <input type="date" value={dateFrom} onChange={e => onFromChange(e.target.value)} className={Style.dateInput} />
      </div>
      <div className={Style.dateRow}>
        <span className={Style.dateRowLabel}>{TEXT.filterDateTo}</span>
        <input type="date" value={dateTo} onChange={e => onToChange(e.target.value)} className={Style.dateInput} />
      </div>
    </div>
  );
}

function FilterPanelFooter({ onClear, onApply }) {
  return (
    <div className={Style.footer}>
      <button onClick={onClear} className={Style.clearBtn}>{TEXT.filterClearAll}</button>
      <button onClick={onApply} className={Style.applyBtn}>{TEXT.filterApply}</button>
    </div>
  );
}

function PillButton({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`${Style.pill} ${active ? Style.pillActive : Style.pillDefault}`}>
      {children}
    </button>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export function FeedbackFiltersPanel({ draft, setDraft, onApply, onClear }) {
  function toggleRating(r) {
    setDraft(prev => ({
      ...prev,
      ratings: prev.ratings.includes(r)
        ? prev.ratings.filter(x => x !== r)
        : [...prev.ratings, r],
    }));
  }

  return (
    <div className={Style.panel}>

      {/* Rating */}
      <div className={Style.section}>
        <FilterSectionLabel>{TEXT.filterRating}</FilterSectionLabel>
        <div className={Style.pillGroup}>
          {RATING_VALUES.map(r => (
            <PillButton key={r} active={draft.ratings.includes(r)} onClick={() => toggleRating(r)}>
              {RATING_LABELS[r]}
            </PillButton>
          ))}
        </div>
      </div>

      {/* Date range */}
      <div className={Style.section}>
        <FilterSectionLabel>{TEXT.filterDate}</FilterSectionLabel>
        <DateRangeSection
          dateFrom={draft.dateFrom}
          dateTo={draft.dateTo}
          onFromChange={v => setDraft(p => ({
            ...p,
            dateFrom: v,
            dateTo: p.dateTo && v && v > p.dateTo ? v : p.dateTo,
          }))}
          onToChange={v => setDraft(p => ({
            ...p,
            dateTo: v,
            dateFrom: p.dateFrom && v && v < p.dateFrom ? v : p.dateFrom,
          }))}
        />
      </div>

      {/* Has message */}
      <div className={Style.sectionLast}>
        <FilterSectionLabel>{TEXT.filterHasMsg}</FilterSectionLabel>
        <div className={Style.pillGroup}>
          {HAS_MESSAGE_OPTIONS.map(opt => (
            <PillButton
              key={opt.value}
              active={draft.hasMessage === opt.value}
              onClick={() => setDraft(p => ({ ...p, hasMessage: opt.value }))}
            >
              {opt.label}
            </PillButton>
          ))}
        </div>
      </div>

      <FilterPanelFooter onClear={onClear} onApply={onApply} />
    </div>
  );
}
